/** Recipe Bank detail (Demo Mode) - a single continuous view of one recipe (image, ingredients,
 * allergens, nutrition, method), distinct from the tabbed RecipeV2Detail page. Internal/
 * authenticated view - ingredient quantities are shown since this isn't a public-facing surface
 * (contrast with the Public Menu recipe view, which hides quantities). Mocked data only, per the
 * Demo Screen Structure pattern - imports directly from src/mocks/data/recipeV2Data.ts. */

import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Chip,
  IconButton,
  Stack,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Section } from '../../components/ui/Section';
import RecipeImageTile from '../../components/shared/RecipeImageTile';
import AllergenChips from '../../components/shared/AllergenChips';
import NutritionalDataTab from '../../components/shared/nutrition/NutritionalDataTab';
import { emptyNutritionalInfo, NutritionSource } from '../../components/shared/nutrition/types';
import { ArrowLeft } from '../../assets';
import { mockRecipeV2Details } from '../../mocks/data/recipeV2Data';

const METHOD_FINAL_STEP_ANCHOR = 'method-final-step';

const RecipeBankDetail = () => {
  const { t } = useTranslation('recipeBank');
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const recipe = useMemo(() => mockRecipeV2Details[Number(id)], [id]);

  const scrollToFinalStep = () => {
    document.getElementById(METHOD_FINAL_STEP_ANCHOR)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  if (!recipe) {
    return (
      <PageArea>
        <Typography variant="textMD">{t('notFound', 'Recipe not found')}</Typography>
      </PageArea>
    );
  }

  const displayName = recipe.customerFacingName ?? recipe.description;
  const steps = recipe.steps ?? [];
  const ingredients = recipe.ingredients ?? [];
  // lazy: no authored/estimated nutrition data exists in the mock catalogue yet - show the
  // clear "not yet available" state rather than fabricate precise figures. Upgrade path: source
  // real values once nutrition capture lands on RecipeV2 (see RecipeV2Detail's Nutrition tab).
  const hasNutrition = false;

  return (
    <PageArea>
      <Stack direction="row"
        alignItems="center"
        spacing={2}
        sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate('/store/recipe-bank')}
          aria-label={t('backToRecipeBank', 'Back to Recipe Bank')}>
          <SvgIcon><ArrowLeft /></SvgIcon>
        </IconButton>
        <PageTitle>{displayName}</PageTitle>
        <Chip label={recipe.primaryCategory}
          size="small"
          variant="outlined" />
      </Stack>

      <Stack spacing={4}>
        <RecipeImageTile
          imageUrl={recipe.imageUrl}
          alt={displayName}
          height={320}
          onClick={steps.length > 0 ? scrollToFinalStep : undefined}
        />

        <Section style={{ p: 4 }}>
          <Typography variant="textLG"
            fontWeight={600}
            sx={{ mb: 2 }}>
            {t('sections.ingredients', 'Ingredients')}
          </Typography>
          {ingredients.length === 0 ? (
            <Typography variant="textSM"
              color="text.secondary">{t('noIngredients', 'No ingredients recorded')}</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('table.ingredient', 'Ingredient')}</TableCell>
                    <TableCell>{t('table.quantity', 'Quantity')}</TableCell>
                    <TableCell>{t('table.preparation', 'Preparation')}</TableCell>
                    <TableCell>{t('table.source', 'Preferred Source')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ingredients.map((ingredient) => (
                    <TableRow key={ingredient.name}>
                      <TableCell>{ingredient.name}</TableCell>
                      <TableCell>{ingredient.quantity} {ingredient.unit}</TableCell>
                      <TableCell>{ingredient.preparation ?? '—'}</TableCell>
                      <TableCell>{ingredient.preferredSource ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Section>

        <Section style={{ p: 4 }}>
          <Typography variant="textLG"
            fontWeight={600}
            sx={{ mb: 2 }}>
            {t('sections.allergens', 'Allergens')}
          </Typography>
          <AllergenChips
            allergens={recipe.allergenSummary}
            showName
            size="medium"
          />
        </Section>

        <Section style={{ p: 4 }}>
          <Typography variant="textLG"
            fontWeight={600}
            sx={{ mb: 2 }}>
            {t('sections.nutrition', 'Nutrition')}
          </Typography>
          {hasNutrition ? (
            <NutritionalDataTab
              nutritionalInfo={emptyNutritionalInfo}
              onChange={() => { /* read-only in Recipe Bank */ }}
              disabled
              source={NutritionSource.Recipe}
            />
          ) : (
            <Typography variant="textSM"
              color="text.secondary">
              {t('nutritionNotAvailable', 'Nutritional information is not yet available for this recipe.')}
            </Typography>
          )}
        </Section>

        <Section style={{ p: 4 }}>
          <Typography variant="textLG"
            fontWeight={600}
            sx={{ mb: 2 }}>
            {t('sections.method', 'Method')}
          </Typography>
          {steps.length === 0 ? (
            <Typography variant="textSM"
              color="text.secondary">{t('noSteps', 'No method steps recorded')}</Typography>
          ) : (
            <Stack spacing={2}>
              {steps.map((step, index) => {
                const isLastStep = index === steps.length - 1;
                return (
                  <Box
                    key={step.id}
                    id={isLastStep ? METHOD_FINAL_STEP_ANCHOR : undefined}
                    sx={{
                      display: 'flex',
                      gap: 2,
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: '12px',
                    }}
                  >
                    {isLastStep && recipe.imageUrl && (
                      <Box
                        component="img"
                        src={recipe.imageUrl}
                        alt={displayName}
                        sx={{ width: 96, height: 96, borderRadius: '8px', objectFit: 'cover', flexShrink: 0 }}
                      />
                    )}
                    <Box>
                      <Chip label={step.stepNumber}
                        size="small"
                        color="primary"
                        sx={{ mb: 1, fontWeight: 700 }} />
                      <Typography variant="textMD"
                        fontWeight={600}>
                        {step.title}
                      </Typography>
                      <Typography variant="textSM"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}>
                        {step.instruction}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Section>
      </Stack>
    </PageArea>
  );
};

export default RecipeBankDetail;
